/**
 * SketchUp stores every length in inches and writes formulas in whatever unit
 * the author picked. Reading one attribute from another therefore crosses two
 * unit systems, and getting the direction wrong silently scales a part by 25.4.
 *
 * The rule, confirmed against the tenant's files: a formula produces a number
 * in the TARGET attribute's `_formulaunits`; reading an attribute yields that
 * attribute's stored inches converted into the SOURCE attribute's own
 * `_formulaunits`. Worked example from "Estrutura Dinâmica":
 *
 *   _lenx_formula      = "parent!lenz - 2*parent!tubo"   (CENTIMETERS)
 *   parent lenz        = 39.685039 in  ->  100.8 cm
 *   parent tubo        = 1.1811024 in  ->    3.0 cm
 *   result             = 94.8 cm       ->  37.322835 in
 *
 * and 37.322834658571416 is exactly what the file stores for lenx.
 */

export type LengthUnit =
  | "INCHES"
  | "FEET"
  | "MILLIMETERS"
  | "CENTIMETERS"
  | "METERS"
  | "YARDS";

/** How many inches one unit of each length is worth. */
const INCHES_PER_UNIT: Record<LengthUnit, number> = {
  INCHES: 1,
  FEET: 12,
  MILLIMETERS: 1 / 25.4,
  CENTIMETERS: 1 / 2.54,
  METERS: 1000 / 25.4,
  YARDS: 36,
};

const ALIASES: Record<string, LengthUnit> = {
  INCH: "INCHES", INCHES: "INCHES", IN: "INCHES", '"': "INCHES",
  FOOT: "FEET", FEET: "FEET", FT: "FEET",
  MILLIMETER: "MILLIMETERS", MILLIMETERS: "MILLIMETERS", MM: "MILLIMETERS",
  CENTIMETER: "CENTIMETERS", CENTIMETERS: "CENTIMETERS", CM: "CENTIMETERS",
  METER: "METERS", METERS: "METERS", M: "METERS",
  YARD: "YARDS", YARDS: "YARDS", YD: "YARDS",
};

/**
 * Units that carry no length at all. A formula tagged with one of these is a
 * plain number or a piece of text and must never be scaled.
 */
const DIMENSIONLESS = new Set(["STRING", "FLOAT", "INTEGER", "BOOLEAN", "PERCENT", "DEGREES", "NONE", ""]);

export function parseLengthUnit(raw: unknown): LengthUnit | null {
  if (typeof raw !== "string") return null;
  const key = raw.trim().toLocaleUpperCase("en-US");
  if (!key || DIMENSIONLESS.has(key)) return null;
  return ALIASES[key] ?? null;
}

export function toInches(value: number, unit: LengthUnit | null): number {
  return unit ? value * INCHES_PER_UNIT[unit] : value;
}

export function fromInches(inches: number, unit: LengthUnit | null): number {
  return unit ? inches / INCHES_PER_UNIT[unit] : inches;
}

export const MILLIMETERS_PER_INCH = 25.4;

export function inchesToMillimeters(inches: number): number {
  return inches * MILLIMETERS_PER_INCH;
}

export function millimetersToInches(millimeters: number): number {
  return millimeters / MILLIMETERS_PER_INCH;
}
